import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from '@reach/router';
import { connect } from 'react-redux';
import { selectDataNotification } from 'state/selectors/notificationCenterSelectors';
import { Loading, NotFound, GlobalContext, I18NContext, I18NConsumer } from 'commons';
import { orgId, setPageTitle } from 'utils';
import * as notificationCenterActions from 'state/actions/notificationCenterActions';
import NoResult from 'containers/notification-center/NoResult';
import Header from './Header';
import Banner from './Banner';
import RtfContent from './RtfContent';
import TextMessage from './TextMessage';
import AttachmentImages from './AttachmentImages';
import AttachmentList from './AttachmentList';
import PolygonMap from './PolygonMap';

const NotificationDetail = ({
  notificationId: id,
  getNotification,
  onClearCache,
  notification: {
    responseCode = 200,
    title, textMessage, rtfContent,
    attachmentFiles = [], priority,
    sendDate: date, drawPolygonSetting,
  },
}) => {
  const i18n = useContext(I18NContext);
  const { organization: { orgName } } = useContext(GlobalContext);

  useEffect(() => {
    if (id) {
      getNotification({ id });
    }

    setTimeout(() => window.scrollTo(0, 0));

    return onClearCache;
  }, []);

  switch (responseCode) {
    case 200:
      break;
    case 404:
      return <Redirect noThrow to={`${orgId}/notif`} />;
    default:
      return <NotFound orgName={orgName} />;
  }

  if (!id) {
    return <Loading />;
  }

  const { searchShapes } = drawPolygonSetting || {};
  const noResult =
    !textMessage &&
    !rtfContent &&
    (!attachmentFiles || attachmentFiles.length === 0) &&
    (!searchShapes || searchShapes.length === 0);

  return (
    <div className="notification-detail">
      <I18NConsumer>
        {() => setPageTitle(`${orgName} - ${i18n['notification.title.text']}- ${title}`)}
      </I18NConsumer>
      <Header {...{ priority, id, title }} />
      <Banner {...{ date, id, title, textMessage }} />
      {noResult && (
        <NoResult
          className="col-sm-8"
          message="general.notification.noneProvided"
        />
      )}
      <div className="row">
        <div className="col-sm-8">
          <div>
            {textMessage && (
              <div eb-test="ee_notificationDetail_text_body" className="pb-4">
                <TextMessage text={textMessage} />
              </div>
            )}
            {rtfContent && <hr />}
            {rtfContent && <RtfContent {...{ title, rtfContent }} />}
          </div>
          {attachmentFiles && (
            <AttachmentImages images={attachmentFiles.filter(file => file.image)} />
          )}
          {attachmentFiles && <AttachmentList files={attachmentFiles} />}
        </div>
        <div className="col-sm-4">
          {searchShapes && searchShapes.length > 0 &&
          searchShapes.findIndex(item => item.shape == null) === -1 && (
            <div eb-test="ee_notificationDetail_field_map">
              <PolygonMap shapes={searchShapes} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

NotificationDetail.propTypes = {
  notificationId: PropTypes.string.isRequired,
  notification: PropTypes.shape({
    [PropTypes.string]: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
  }).isRequired,
  getNotification: PropTypes.func.isRequired,
  onClearCache: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  notification: selectDataNotification(state),
});

const mapDispatchToProps = dispatch => ({
  getNotification: ({ id }) => dispatch(notificationCenterActions.getNotification.action({ id })),
  onClearCache: () => dispatch(notificationCenterActions.onClearCache({
    notification: { responseCode: 200 },
  })),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationDetail);
